/**
 * Step 8 — Cybersecurity Concept (Controls + Requirements + V&V).
 *
 * Outer-tab layout (Story 14.A): the two collections + their
 * hierarchical Current/Child split + V&V are all rendered as flat
 * outer tabs via TableTabNav.
 *
 *   non-hierarchical: [Controls (N)] [Requirements (N)] [V&V Recommendations]
 *   hierarchical:     [Controls · Current TARA (N)] [Controls · Child TARAs (M)]
 *                     [Requirements · Current TARA (N)] [Requirements · Child TARAs (M)]
 *                     [V&V Recommendations]
 *
 * V&V never has a Current/Child split (not hierarchical-tagged).
 */
import React, { useMemo, useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import TableTabNav from '../components/TableTabNav/TableTabNav';
import { useStepSection, resolveSectionKey } from '../hooks/useStepSection';
import ControlsTable from './step8-concept/ControlsTable';
import ControlFormModal from './step8-concept/ControlFormModal';
import ChildControlsTable, { type ChildControlView } from './step8-concept/ChildControlsTable';
import RequirementsTable from './step8-concept/RequirementsTable';
import RequirementFormModal from './step8-concept/RequirementFormModal';
import ChildRequirementsTable, { type ChildRequirementView } from './step8-concept/ChildRequirementsTable';
import VVSection from './step8-concept/VVSection';
import type { ControlRecord } from './step8-concept/controlsFieldSchema';
import type { RequirementRecord } from './step8-concept/requirementsFieldSchema';
import {
  exportControlsToCsv,
  exportRequirementsToCsv,
  parseControlsFile,
  parseRequirementsFile,
} from './step8-concept/controlsRequirementsCsv';
import type { Step8BodyProps } from './stepBodyProps';
import { notify } from '../../../services/notificationBus';
import styles from './Step8CybersecurityConcept.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;

const TAB_CONTROLS = 'controls';
const TAB_CONTROLS_CURRENT = 'controls:current';
const TAB_CONTROLS_CHILD = 'controls:child';
const TAB_REQS = 'requirements';
const TAB_REQS_CURRENT = 'requirements:current';
const TAB_REQS_CHILD = 'requirements:child';
const TAB_VV = 'vv';

type ControlModalState = null | { kind: 'add' } | { kind: 'edit'; control: ControlRecord };
type ReqModalState = null | { kind: 'add' } | { kind: 'edit'; req: RequirementRecord };

export default function Step8CybersecurityConcept({
  status,
  editable,
  structured,
  controls,
  requirements,
  crud,
  goalOptions,
  controlOptions,
  controlsHierarchical,
  requirementsHierarchical,
  threadId,
  analysisMode,
  errorMessage,
}: Step8BodyProps) {
  const [controlModal, setControlModal] = useState<ControlModalState>(null);
  const [reqModal, setReqModal] = useState<ReqModalState>(null);
  const { confirm, confirmDialog } = useConfirm();
  // V&V tab — outer-tab navigation between Controls / Requirements /
  // V&V Recommendations. V&V data is owned here (vs the hook) since
  // the legacy VVGenerationCard manages its own polling lifecycle
  // and fires onGenerationComplete with the full payload.
  const [activeTab, setActiveTab] = useStepSection(TAB_CONTROLS);
  const [vvData, setVvData] = useState<any>(null);

  // NOTE: hooks must be called unconditionally. All useMemo calls live
  // above the early returns below so the hook count stays stable as
  // status flips between generating / draft / failed.
  const controlsTyped = controls as ControlRecord[];
  const requirementsTyped = requirements as RequirementRecord[];

  const childControlsRaw: ChildControlView[] = controlsHierarchical.enabled
    ? controlsTyped.filter((c: any) => c?.source === 'child_tara')
    : [];
  const childReqsRaw: ChildRequirementView[] = requirementsHierarchical.enabled
    ? requirementsTyped.filter((r: any) => r?.source === 'child_tara')
    : [];

  const childControlView: ChildControlView[] = useMemo(
    () =>
      childControlsRaw.map((c) => ({
        ...c,
        is_relevant:
          controlsHierarchical.modifications[c.control_id]?.is_relevant ??
          c.is_relevant ??
          true,
        relevance_justification:
          controlsHierarchical.modifications[c.control_id]?.relevance_justification ??
          c.relevance_justification ??
          '',
      })),
    [childControlsRaw, controlsHierarchical.modifications],
  );
  const childReqView: ChildRequirementView[] = useMemo(
    () =>
      childReqsRaw.map((c) => ({
        ...c,
        is_relevant:
          requirementsHierarchical.modifications[c.req_id]?.is_relevant ??
          c.is_relevant ??
          true,
        relevance_justification:
          requirementsHierarchical.modifications[c.req_id]?.relevance_justification ??
          c.relevance_justification ??
          '',
      })),
    [childReqsRaw, requirementsHierarchical.modifications],
  );

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Cybersecurity concept generation failed"
        description={errorMessage ?? structured.error ?? 'The cybersecurity concept agent encountered an error. Revise and retry.'}
      />
    );
  }

  const noDataYet =
    controlsTyped.length === 0 && requirementsTyped.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Generating cybersecurity concept"
        description="The agent is deriving controls + requirements per ISO 21434 Clause 9.5. This usually takes 1–2 minutes."
      />
    );
  }

  /* ----- Controls handlers ----- */

  const handleControlSave = async (values: Record<string, unknown>) => {
    try {
      if (controlModal?.kind === 'edit') {
        await crud.update(controlModal.control.control_id, values);
        notify({ type: 'success', message: `Control "${values.control_id}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Control "${values.control_id}" added.` });
      }
      setControlModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save control';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save control');
    }
  };

  const handleControlDelete = async (target: ControlRecord) => {
    const ok = await confirm({
      title: 'Delete control?',
      description: `Delete control "${target.control_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.control_id);
      notify({ type: 'success', message: `Control "${target.control_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      notify({ type: 'error', title: 'Delete failed', message: e?.message || 'Failed to delete control' });
    }
  };

  const handleControlsExport = () => {
    exportControlsToCsv(
      controlsHierarchical.enabled
        ? controlsTyped.filter((c: any) => c?.source !== 'child_tara')
        : controlsTyped,
    );
  };

  const handleControlsImport = async (file: File) => {
    let records: ControlRecord[];
    try { records = await parseControlsFile(file); } catch (e: any) {
      notify({ type: 'error', title: 'Import failed', message: e?.message || `Could not parse ${file.name}` });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no control rows.` });
      return;
    }
    const existingIds = new Set(controlsTyped.map((c) => c.control_id));
    let added = 0, skipped = 0, failed = 0;
    for (const record of records) {
      if (!record?.control_id) { failed += 1; continue; }
      if (existingIds.has(record.control_id)) { skipped += 1; continue; }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.control_id);
        added += 1;
      } catch { failed += 1; }
    }
    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  /* ----- Requirements handlers ----- */

  const handleReqSave = async (values: Record<string, unknown>) => {
    try {
      if (reqModal?.kind === 'edit') {
        await crud.update(reqModal.req.req_id, values);
        notify({ type: 'success', message: `Requirement "${values.req_id}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Requirement "${values.req_id}" added.` });
      }
      setReqModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save requirement';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save requirement');
    }
  };

  const handleReqDelete = async (target: RequirementRecord) => {
    const ok = await confirm({
      title: 'Delete requirement?',
      description: `Delete requirement "${target.req_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.req_id);
      notify({ type: 'success', message: `Requirement "${target.req_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      notify({ type: 'error', title: 'Delete failed', message: e?.message || 'Failed to delete requirement' });
    }
  };

  const handleReqsExport = () => {
    exportRequirementsToCsv(
      requirementsHierarchical.enabled
        ? requirementsTyped.filter((r: any) => r?.source !== 'child_tara')
        : requirementsTyped,
    );
  };

  const handleReqsImport = async (file: File) => {
    let records: RequirementRecord[];
    try { records = await parseRequirementsFile(file); } catch (e: any) {
      notify({ type: 'error', title: 'Import failed', message: e?.message || `Could not parse ${file.name}` });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no requirement rows.` });
      return;
    }
    const existingIds = new Set(requirementsTyped.map((r) => r.req_id));
    let added = 0, skipped = 0, failed = 0;
    for (const record of records) {
      if (!record?.req_id) { failed += 1; continue; }
      if (existingIds.has(record.req_id)) { skipped += 1; continue; }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.req_id);
        added += 1;
      } catch { failed += 1; }
    }
    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  /* ----- Hierarchical splits ----- */

  const newControls = controlsHierarchical.enabled
    ? controlsTyped.filter((c: any) => c?.source !== 'child_tara')
    : controlsTyped;
  const newReqs = requirementsHierarchical.enabled
    ? requirementsTyped.filter((r: any) => r?.source !== 'child_tara')
    : requirementsTyped;
  const childControls = childControlsRaw;
  const childReqs = childReqsRaw;

  const handleControlToggle = (controlId: string) => {
    const current = childControlView.find((c) => c.control_id === controlId);
    controlsHierarchical.setRelevance(controlId, !(current?.is_relevant !== false));
  };
  const handleReqToggle = (reqId: string) => {
    const current = childReqView.find((c) => c.req_id === reqId);
    requirementsHierarchical.setRelevance(reqId, !(current?.is_relevant !== false));
  };

  const hierarchical = controlsHierarchical.enabled; // both flags in sync
  const tabs = hierarchical
    ? [
        { key: TAB_CONTROLS_CURRENT, label: `Controls · Current TARA (${newControls.length})` },
        { key: TAB_CONTROLS_CHILD, label: `Controls · Child TARAs (${childControls.length})` },
        { key: TAB_REQS_CURRENT, label: `Requirements · Current TARA (${newReqs.length})` },
        { key: TAB_REQS_CHILD, label: `Requirements · Child TARAs (${childReqs.length})` },
        { key: TAB_VV, label: 'V&V Recommendations' },
      ]
    : [
        { key: TAB_CONTROLS, label: `Controls (${controlsTyped.length})` },
        { key: TAB_REQS, label: `Requirements (${requirementsTyped.length})` },
        { key: TAB_VV, label: 'V&V Recommendations' },
      ];

  const activeKey = resolveSectionKey(tabs, activeTab);

  const childInputsDisabledControls = controlsHierarchical.approving || !editable;
  const childInputsDisabledReqs = requirementsHierarchical.approving || !editable;

  return (
    <div className={styles.bleed}>
      <TableTabNav
        tabs={tabs}
        activeKey={activeKey}
        onChange={setActiveTab}
        ariaLabel="Cybersecurity Concept tabs"
        bleed={false}
        sticky={false}
      />

      {(activeKey === TAB_CONTROLS || activeKey === TAB_CONTROLS_CURRENT) && (
        <ControlsTable
          controls={hierarchical ? newControls : controlsTyped}
          editable={editable}
          onAdd={() => setControlModal({ kind: 'add' })}
          onEdit={(control) => setControlModal({ kind: 'edit', control })}
          onDelete={(control) => handleControlDelete(control)}
          onExport={handleControlsExport}
          onImport={handleControlsImport}
          emptyMessage={hierarchical ? 'No new controls yet for this TARA.' : undefined}
        />
      )}

      {activeKey === TAB_CONTROLS_CHILD && (
        <ChildControlsTable
          controls={childControlView}
          onToggleRelevance={handleControlToggle}
          onJustificationChange={controlsHierarchical.setJustification}
          disabled={childInputsDisabledControls}
          onSave={controlsHierarchical.onApprove}
        />
      )}

      {(activeKey === TAB_REQS || activeKey === TAB_REQS_CURRENT) && (
        <RequirementsTable
          requirements={hierarchical ? newReqs : requirementsTyped}
          editable={editable}
          onAdd={() => setReqModal({ kind: 'add' })}
          onEdit={(req) => setReqModal({ kind: 'edit', req })}
          onDelete={(req) => handleReqDelete(req)}
          onExport={handleReqsExport}
          onImport={handleReqsImport}
          emptyMessage={hierarchical ? 'No new requirements yet for this TARA.' : undefined}
        />
      )}

      {activeKey === TAB_REQS_CHILD && (
        <ChildRequirementsTable
          requirements={childReqView}
          onToggleRelevance={handleReqToggle}
          onJustificationChange={requirementsHierarchical.setJustification}
          disabled={childInputsDisabledReqs}
          onSave={requirementsHierarchical.onApprove}
        />
      )}

      {activeKey === TAB_VV && (
        <VVSection
          threadId={threadId}
          analysisMode={analysisMode}
          data={vvData}
          onDataChange={setVvData}
          // generating/revising/empty/failed already short-circuit to the
          // spinner above, so by here status is only draft|approved — VV is
          // never rendered in a busy state.
          disabled={false}
        />
      )}

      {/* Modals */}
      {controlModal && (
        <ControlFormModal
          mode={controlModal.kind}
          initialControl={controlModal.kind === 'edit' ? controlModal.control : null}
          goalOptions={goalOptions}
          onSubmit={handleControlSave}
          onClose={() => setControlModal(null)}
        />
      )}
      {reqModal && (
        <RequirementFormModal
          mode={reqModal.kind}
          initialReq={reqModal.kind === 'edit' ? reqModal.req : null}
          controlOptions={controlOptions}
          onSubmit={handleReqSave}
          onClose={() => setReqModal(null)}
        />
      )}

      {/* Delete confirms — one hook drives both (only one open at a time) */}
      {confirmDialog}
    </div>
  );
}
